/**
 * Lift2Fit — Manage coach · Booking tab.
 *
 * The lifter's side of the coach's diary with a mode switch:
 *
 *   * **List** — today's behaviour: the coach's upcoming open slots with
 *     `booked / capacity`, book, and the lifter's own bookings to cancel.
 *   * **Calendar** — a real month grid of the same availability. Each day shows
 *     compact chips (the coach calendar's own visual language: a coloured left
 *     edge and the exact count), a full slot is unmistakably not bookable, and
 *     clicking a slot opens its detail where booking is confirmed.
 *
 * Cancelling works from both modes and from "my bookings". Availability is
 * re-read after every write, so the count the lifter sees is the count the coach
 * sees.
 */
import { useEffect, useMemo, useState } from 'react';
import { api, type CrmBooking, type CrmSlot, type CoachPackSubscription } from '@/lib/api';
import { cn } from '@/lib/format';
import { Button } from '@/components/ui';
import { TimerIcon } from '@/lib/icons';
import { LiveMessage, Panel, StatusChip } from '../components';
import {
  CrmMessage,
  addDays,
  bookingStatusMeta,
  formatDayHeading,
  formatSlotRange,
  isSameLocalDay,
  slotKindLabel,
  startOfIsoWeek,
  toDateInput,
} from '../crm/shared';

const AVAILABILITY_DAYS = 28;
const CALENDAR_WEEKS = 6;

function startOfMonth(date: Date): Date {
  return new Date(date.getFullYear(), date.getMonth(), 1);
}

/** "Mon 3 Nov · 09:00–10:00" in the lifter's own locale. */
function slotWindow(startsAt: string, endsAt: string): string {
  const start = new Date(startsAt);
  if (Number.isNaN(start.getTime())) return '—';
  return `${formatDayHeading(start)} · ${formatSlotRange(startsAt, endsAt)}`;
}

type BookingMode = 'list' | 'calendar';
type Tone = 'info' | 'success' | 'danger';

export function BookingTab({ coachName, linked }: { coachName: string; linked: boolean }) {
  const [mode, setMode] = useState<BookingMode>('list');
  const [anchor, setAnchor] = useState<Date>(() => new Date());
  const [slots, setSlots] = useState<CrmSlot[]>([]);
  const [bookings, setBookings] = useState<CrmBooking[]>([]);
  const [subscription, setSubscription] = useState<CoachPackSubscription | null>(null);
  const [locked, setLocked] = useState(false);
  const [loading, setLoading] = useState(true);
  const [loaded, setLoaded] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const [tone, setTone] = useState<Tone>('info');
  const [selectedSlotId, setSelectedSlotId] = useState<string | null>(null);
  const [confirmingBookId, setConfirmingBookId] = useState<string | null>(null);
  const [nonce, setNonce] = useState(0);

  const gridStart = useMemo(() => startOfIsoWeek(startOfMonth(anchor)), [anchor]);
  const gridEnd = useMemo(() => addDays(gridStart, CALENDAR_WEEKS * 7), [gridStart]);
  const gridStartIso = gridStart.toISOString();

  useEffect(() => {
    let alive = true;
    setLoading(true);
    const now = new Date();
    const listEnd = addDays(now, AVAILABILITY_DAYS);
    const from = mode === 'calendar' ? gridStart : now;
    const to = mode === 'calendar' ? gridEnd : listEnd;
    const bookingsTo = to.getTime() > listEnd.getTime() ? to : listEnd;
    Promise.all([
      api.crmCoachAvailability(from.toISOString(), to.toISOString()),
      api.crmMyBookings(now.toISOString(), bookingsTo.toISOString()),
    ])
      .then(([availability, mine]) => {
        if (!alive) return;
        const problem = availability.problem ?? mine.problem;
        if (problem) {
          setError(problem.detail ?? problem.title);
        } else {
          setSlots(availability.data?.data ?? []);
          setBookings(mine.data?.data ?? []);
          setSubscription(availability.data?.subscription ?? null);
          setLocked(availability.data?.locked ?? false);
          setError(null);
        }
        setLoading(false);
        setLoaded(true);
      })
      .catch((cause: unknown) => {
        if (!alive) return;
        setError(cause instanceof Error ? cause.message : 'Could not read your coach’s availability.');
        setLoading(false);
        setLoaded(true);
      });
    return () => {
      alive = false;
    };
    // `gridStartIso` stands in for the memoised gridStart/gridEnd pair.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [nonce, mode, gridStartIso]);

  const reload = () => setNonce((value) => value + 1);

  const book = async (slot: CrmSlot) => {
    setBusy(slot.slotId);
    setTone('info');
    setMessage('Booking…');
    const result = await api.crmBookMyPlace(slot.slotId);
    setBusy(null);
    setConfirmingBookId(null);
    if (result.problem || !result.data) {
      setTone('danger');
      setMessage(result.problem?.detail ?? 'That slot was not booked.');
      return;
    }
    setTone('success');
    setMessage(`Booked ${slotWindow(slot.startsAt, slot.endsAt)} — it is on your coach’s calendar now.`);
    reload();
  };

  const cancel = async (booking: CrmBooking, busyKey: string) => {
    setBusy(busyKey);
    setTone('info');
    setMessage('Cancelling…');
    const result = await api.crmCancelBooking(booking.bookingId);
    setBusy(null);
    if (result.problem || !result.data) {
      setTone('danger');
      setMessage(result.problem?.detail ?? 'That booking was not cancelled.');
      return;
    }
    setTone('success');
    setMessage('Booking cancelled — the place is free again.');
    reload();
  };

  /**
   * A slot is bookable only when the lifter holds THAT slot's pack (0025). The
   * API already withholds anything else from the availability read and refuses
   * the write, so this is the belt-and-braces label for a stale tab. An open
   * (unscoped) slot is not part of any pack and is never self-bookable.
   */
  const packBlock = (slot: CrmSlot): string | null => {
    if (slot.packId === null) {
      return 'This is open availability, not part of your coaching pack. Book only the sessions your pack includes.';
    }
    if (subscription?.packId === slot.packId && subscription.status === 'active') return null;
    const name = slot.packName ?? 'the pack';
    if (subscription) {
      return `This slot is for the "${name}" pack. You hold "${subscription.packName}" — switch packs on the Discover tab to book it.`;
    }
    return `This slot is for the "${name}" pack, and you do not hold it yet. Subscribe on the Discover tab to book it.`;
  };

  const bookingBySlot = new Map(bookings.map((booking) => [booking.slotId, booking]));

  /**
   * The subscribed pack's slots are the lifter's obvious choice (feature 0023):
   * they are listed first, then open availability, then other coaches' packs.
   * A booked slot sorts by its own time inside each group.
   */
  const packRank = (slot: CrmSlot): number => {
    if (subscription && slot.packId === subscription.packId) return 0;
    if (slot.packId === null) return 1;
    return 2;
  };
  const orderedSlots = [...slots].sort(
    (a, b) => packRank(a) - packRank(b) || Date.parse(a.startsAt) - Date.parse(b.startsAt),
  );
  const nextPackSlot =
    orderedSlots.find(
      (slot) =>
        subscription !== null &&
        slot.packId === subscription.packId &&
        slot.status === 'open' &&
        slot.booked < slot.capacity &&
        !bookingBySlot.has(slot.slotId),
    ) ?? null;

  const selectedSlot = selectedSlotId ? slots.find((slot) => slot.slotId === selectedSlotId) ?? null : null;
  const selectedBooking = selectedSlot ? bookingBySlot.get(selectedSlot.slotId) ?? null : null;
  const selectedBlock = selectedSlot ? packBlock(selectedSlot) : null;

  const changeMode = (next: BookingMode) => {
    setMode(next);
    setMessage(null);
    setSelectedSlotId(null);
    setConfirmingBookId(null);
  };

  /* --------------------------------- list -------------------------------- */

  const listView = slots.length ? (
    <div className="flex flex-col gap-3">
      {subscription ? (
        <p
          className="border-l-2 border-accent bg-page-subtle px-3 py-2 text-[13px] text-ink"
          data-testid="coach-pack-hint"
        >
          Your pack is <span className="font-bold">{subscription.packName}</span> — its slots are
          listed first.{' '}
          {nextPackSlot
            ? `Next available ${slotWindow(nextPackSlot.startsAt, nextPackSlot.endsAt)}.`
            : 'No open slot for it in the next 28 days yet.'}
        </p>
      ) : null}
      <ul className="flex flex-col gap-1.5" data-testid="coach-open-slots">
      {orderedSlots.map((slot) => {
        const full = slot.booked >= slot.capacity;
        const booking = bookingBySlot.get(slot.slotId);
        const block = packBlock(slot);
        const isSubscribedPack = subscription !== null && slot.packId === subscription.packId;
        return (
          <li
            key={slot.slotId}
            data-testid={`coach-slot-${slot.slotId}`}
            className="flex flex-wrap items-center justify-between gap-2 border border-hairline px-3 py-2"
          >
            <span className="flex min-w-0 flex-col">
              <span className="text-[13px] font-bold text-ink">{slotWindow(slot.startsAt, slot.endsAt)}</span>
              <span className="text-[11px] text-ink-muted">
                {slotKindLabel(slot.kind)}
                {slot.location ? ` · ${slot.location}` : ''}
                {slot.packName ? ` · ${slot.packName} pack` : ''}
              </span>
            </span>
            <span className="flex items-center gap-2">
              {isSubscribedPack ? <StatusChip tone="accent">Your pack</StatusChip> : null}
              <span
                data-testid={`coach-slot-count-${slot.slotId}`}
                className={cn('l2f-tabular font-mono text-[13px] font-bold', full ? 'text-danger' : 'text-ink')}
              >
                {slot.booked}/{slot.capacity}
              </span>
              {booking ? (
                <>
                  <StatusChip tone="success">Booked</StatusChip>
                  <Button
                    size="sm"
                    variant="outline"
                    data-testid={`coach-booking-cancel-${booking.bookingId}`}
                    disabled={busy === slot.slotId}
                    onClick={() => void cancel(booking, slot.slotId)}
                  >
                    Cancel
                  </Button>
                </>
              ) : full ? (
                <StatusChip tone="danger">Full</StatusChip>
              ) : block ? (
                <>
                  <StatusChip tone="warning">Pack only</StatusChip>
                  <Button
                    size="sm"
                    variant="outline"
                    disabled
                    title={block}
                    data-testid={`coach-slot-blocked-${slot.slotId}`}
                  >
                    Needs {slot.packName ?? 'pack'}
                  </Button>
                </>
              ) : (
                <Button
                  size="sm"
                  variant="secondary"
                  data-testid={`coach-slot-book-${slot.slotId}`}
                  disabled={busy === slot.slotId}
                  onClick={() => void book(slot)}
                >
                  Book
                </Button>
              )}
            </span>
          </li>
        );
      })}
      </ul>
    </div>
  ) : (
    <p className="text-[13px] text-ink-muted" data-testid="coach-no-open-slots">
      No open slots in the next {AVAILABILITY_DAYS} days. Your coach can add more availability.
    </p>
  );

  /* ------------------------------- calendar ------------------------------ */

  const slotsByDay = useMemo(() => {
    const map = new Map<string, CrmSlot[]>();
    for (const slot of slots) {
      const key = toDateInput(new Date(slot.startsAt));
      const list = map.get(key) ?? [];
      list.push(slot);
      map.set(key, list);
    }
    return map;
  }, [slots]);

  const days = useMemo(
    () => Array.from({ length: CALENDAR_WEEKS * 7 }, (_, index) => addDays(gridStart, index)),
    [gridStart],
  );
  const monthLabel = anchor.toLocaleDateString('en-GB', { month: 'long', year: 'numeric' });

  const calendarView = (
    <div className="flex flex-col gap-3" data-testid="my-coach-calendar">
      <div className="flex flex-wrap items-center justify-between gap-3 border border-hairline bg-surface p-3">
        <div className="flex flex-wrap items-center gap-2">
          <Button
            size="sm"
            variant="outline"
            data-testid="my-coach-calendar-prev"
            onClick={() => setAnchor(new Date(anchor.getFullYear(), anchor.getMonth() - 1, 1))}
          >
            ‹ Prev
          </Button>
          <Button size="sm" variant="outline" data-testid="my-coach-calendar-today" onClick={() => setAnchor(new Date())}>
            Today
          </Button>
          <Button
            size="sm"
            variant="outline"
            data-testid="my-coach-calendar-next"
            onClick={() => setAnchor(new Date(anchor.getFullYear(), anchor.getMonth() + 1, 1))}
          >
            Next ›
          </Button>
          <span className="l2f-tabular font-mono text-[13px] font-bold text-ink" data-testid="my-coach-calendar-range">
            {monthLabel}
          </span>
        </div>
        <span className="text-[11px] text-ink-muted">
          Open slots only · a full slot is shown but cannot be booked
        </span>
      </div>

      <div className="grid grid-cols-7 gap-px border border-hairline bg-hairline">
        {['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'].map((label) => (
          <div
            key={label}
            className="bg-page-subtle px-1 py-1.5 text-center text-[10px] font-bold uppercase tracking-[0.08em] text-ink-muted"
          >
            {label}
          </div>
        ))}
        {days.map((day) => {
          const key = toDateInput(day);
          const daySlots = slotsByDay.get(key) ?? [];
          const inMonth = day.getMonth() === anchor.getMonth();
          const isToday = isSameLocalDay(day, new Date());
          return (
            <section
              key={key}
              data-testid={`my-coach-day-${key}`}
              aria-label={formatDayHeading(day)}
              className={cn(
                'flex min-h-[86px] min-w-0 flex-col gap-1 bg-surface p-1.5',
                !inMonth && 'opacity-55',
                isToday && 'outline outline-1 -outline-offset-1 outline-accent',
              )}
            >
              <span className="flex items-center justify-between text-[10px] font-bold uppercase tracking-[0.06em] text-ink-muted">
                {day.getDate()}
                {isToday ? <span className="text-accent">Today</span> : null}
              </span>
              {daySlots.map((slot) => {
                const full = slot.booked >= slot.capacity;
                const booked = Boolean(bookingBySlot.get(slot.slotId));
                const block = packBlock(slot);
                const state = booked ? 'booked' : full ? 'full' : block ? 'blocked' : slot.booked > 0 ? 'partial' : 'open';
                return (
                  <button
                    key={slot.slotId}
                    type="button"
                    data-testid={`my-coach-chip-${slot.slotId}`}
                    data-status={state}
                    aria-label={`${formatSlotRange(slot.startsAt, slot.endsAt)} · ${slot.booked} of ${slot.capacity} places taken${full ? ' · Full' : ''}${booked ? ' · Booked' : ''}${slot.packName ? ` · ${slot.packName} pack` : ''}${block ? ' · Pack only' : ''}`}
                    onClick={() => {
                      setSelectedSlotId(slot.slotId);
                      setConfirmingBookId(null);
                    }}
                    className={cn(
                      'flex w-full min-w-0 flex-col items-start gap-0.5 border border-hairline border-l-2 bg-page-subtle px-1.5 py-1 text-left transition-colors duration-150 hover:border-accent',
                      state === 'full'
                        ? 'border-l-danger'
                        : state === 'booked'
                          ? 'border-l-success'
                          : state === 'blocked'
                            ? 'border-l-warning'
                            : state === 'partial'
                              ? 'border-l-accent'
                              : 'border-l-hairline',
                    )}
                  >
                    <span className="flex w-full min-w-0 items-center justify-between gap-1">
                      <span className="l2f-tabular truncate font-mono text-[10px] font-bold text-ink">
                        {formatSlotRange(slot.startsAt, slot.endsAt)}
                      </span>
                      <span
                        data-testid={`my-coach-chip-count-${slot.slotId}`}
                        className={cn(
                          'l2f-tabular shrink-0 font-mono text-[10px] font-bold',
                          full ? 'text-danger' : state === 'booked' ? 'text-success' : 'text-ink-muted',
                        )}
                      >
                        {slot.booked}/{slot.capacity}
                      </span>
                    </span>
                    {booked ? (
                      <span className="text-[9px] font-bold uppercase tracking-[0.06em] text-success">Booked</span>
                    ) : full ? (
                      <span className="text-[9px] font-bold uppercase tracking-[0.06em] text-danger">Full</span>
                    ) : block ? (
                      <span className="truncate text-[9px] font-bold uppercase tracking-[0.06em] text-warning">
                        Pack only
                      </span>
                    ) : null}
                  </button>
                );
              })}
            </section>
          );
        })}
      </div>

      {selectedSlot ? (
        <Panel testId="my-coach-slot-detail" title="Slot detail" subtitle={slotWindow(selectedSlot.startsAt, selectedSlot.endsAt)}>
          <div className="flex flex-wrap items-center gap-2">
            <StatusChip tone={selectedSlot.kind === 'group' ? 'accent' : 'neutral'}>
              {slotKindLabel(selectedSlot.kind)}
            </StatusChip>
            <span className="l2f-tabular font-mono text-[13px] font-bold text-ink">
              {selectedSlot.booked}/{selectedSlot.capacity} booked
            </span>
            {selectedSlot.location ? <span className="text-[12px] text-ink-muted">{selectedSlot.location}</span> : null}
            {selectedSlot.packName ? (
              <StatusChip tone="info">{selectedSlot.packName} pack</StatusChip>
            ) : null}
          </div>

          {selectedBooking ? (
            <div className="flex flex-wrap items-center gap-2">
              <StatusChip tone="success">You are booked</StatusChip>
              <Button
                size="sm"
                variant="outline"
                data-testid={`my-coach-slot-detail-cancel-${selectedBooking.bookingId}`}
                disabled={busy === selectedSlot.slotId}
                onClick={() => void cancel(selectedBooking, selectedSlot.slotId)}
              >
                Cancel this booking
              </Button>
            </div>
          ) : selectedSlot.booked >= selectedSlot.capacity ? (
            <p className="border-l-2 border-danger bg-page-subtle px-3 py-2 text-[13px] text-danger" data-testid="my-coach-slot-detail-full">
              Full — all {selectedSlot.capacity} places are taken. This slot cannot be booked.
            </p>
          ) : selectedBlock ? (
            <p
              className="border-l-2 border-warning bg-page-subtle px-3 py-2 text-[13px] text-ink"
              data-testid="my-coach-slot-detail-pack"
            >
              {selectedBlock}
            </p>
          ) : confirmingBookId === selectedSlot.slotId ? (
            <div className="flex flex-col gap-2 border border-accent bg-page-subtle p-3" data-testid="my-coach-book-confirm">
              <p className="text-[13px] font-bold text-ink">
                Book {slotWindow(selectedSlot.startsAt, selectedSlot.endsAt)}?
              </p>
              <p className="text-[12px] text-ink-muted">
                It will hold one of {selectedSlot.capacity} places and appear on your coach’s calendar.
              </p>
              <div className="flex flex-wrap gap-2">
                <Button
                  size="sm"
                  data-testid={`my-coach-book-confirm-${selectedSlot.slotId}`}
                  disabled={busy === selectedSlot.slotId}
                  onClick={() => void book(selectedSlot)}
                >
                  {busy === selectedSlot.slotId ? 'Booking…' : 'Yes, book it'}
                </Button>
                <Button size="sm" variant="ghost" onClick={() => setConfirmingBookId(null)}>
                  Not yet
                </Button>
              </div>
            </div>
          ) : (
            <Button
              size="sm"
              variant="secondary"
              data-testid={`my-coach-slot-detail-book-${selectedSlot.slotId}`}
              disabled={busy === selectedSlot.slotId}
              onClick={() => setConfirmingBookId(selectedSlot.slotId)}
            >
              Book this slot
            </Button>
          )}
        </Panel>
      ) : (
        <p className="text-[12px] text-ink-muted">Pick a slot on the grid to see its detail.</p>
      )}
    </div>
  );

  /* -------------------------------- render ------------------------------- */

  const myBookings = bookings.length ? (
    <div className="flex flex-col gap-1.5" data-testid="coach-my-bookings">
      <h4 className="text-[11px] font-bold uppercase tracking-[0.1em] text-ink-muted">Your booked sessions</h4>
      {bookings.map((booking) => {
        const meta = bookingStatusMeta(booking.status);
        return (
          <div
            key={booking.bookingId}
            data-testid={`coach-my-booking-${booking.bookingId}`}
            className="flex flex-wrap items-center justify-between gap-2 border-l-2 border-l-accent bg-page-subtle px-3 py-2"
          >
            <span className="flex flex-wrap items-center gap-2">
              <span className="text-[12px] font-bold text-ink">
                {slotWindow(booking.slot.startsAt, booking.slot.endsAt)}
              </span>
              <StatusChip tone={meta.tone}>{meta.label}</StatusChip>
            </span>
            <Button
              size="sm"
              variant="ghost"
              data-testid={`coach-my-booking-cancel-${booking.bookingId}`}
              disabled={busy !== null}
              onClick={() => void cancel(booking, booking.bookingId)}
            >
              Cancel booking
            </Button>
          </div>
        );
      })}
    </div>
  ) : null;

  if (!linked) {
    return (
      <Panel testId="my-coach-booking-tab" title="Booking" subtitle="Book a session with your coach.">
        <p className="text-[13px] text-ink-muted" data-testid="booking-no-coach">
          No coach linked yet, so there is no availability to book. Connect a coach on the Coach tab first.
        </p>
      </Panel>
    );
  }

  // Connected but unpaid (0025): the coach is linked and chat works, but the
  // diary is withheld until the lifter holds a pack. Say so plainly — an empty
  // calendar with no reason would read as "your coach has no slots".
  const lockedView = (
    <div className="flex flex-col gap-2" data-testid="coach-calendar-locked">
      <p className="text-[13px] font-bold text-ink">{coachName}’s calendar is not visible yet.</p>
      <p className="text-[13px] text-ink-muted">
        Connecting lets you chat with {coachName}, but their diary opens only once you hold one of their
        coaching packs — and then only the sessions that pack includes. Open the Discover tab to see their packs
        and subscribe.
      </p>
    </div>
  );

  return (
    <div className="flex flex-col gap-4" data-testid="my-coach-booking-tab">
      <Panel
        title={`Book a session with ${coachName}`}
        subtitle="Switch between the slot list and the coach's month calendar. Capacity is the coach's rule: a full slot is shown but never bookable."
        actions={
          <span className="flex flex-wrap items-center gap-1" role="group" aria-label="Booking view">
            <Button
              size="sm"
              variant={mode === 'list' ? 'secondary' : 'outline'}
              aria-pressed={mode === 'list'}
              data-testid="my-coach-mode-list"
              onClick={() => changeMode('list')}
            >
              List
            </Button>
            <Button
              size="sm"
              variant={mode === 'calendar' ? 'secondary' : 'outline'}
              aria-pressed={mode === 'calendar'}
              data-testid="my-coach-mode-calendar"
              onClick={() => changeMode('calendar')}
            >
              Calendar
            </Button>
            <Button size="sm" variant="ghost" onClick={reload} disabled={loading}>
              Refresh
            </Button>
          </span>
        }
      >
        <div data-testid="coach-availability" className="contents">
          {loading && !loaded ? (
            <p className="text-[13px] text-ink-muted" aria-live="polite">
              Loading availability…
            </p>
          ) : error ? (
            <LiveMessage message={error} tone="danger" />
          ) : locked ? (
            lockedView
          ) : mode === 'list' ? (
            listView
          ) : (
            calendarView
          )}
        </div>
      </Panel>

      {myBookings}

      <CrmMessage message={message} tone={tone} testId="my-coach-booking-message" />
      <p className="flex items-center gap-2 text-[12px] text-ink-muted">
        <TimerIcon className="text-[14px]" />
        Booking and availability only. Refusals come from the API and are shown word for word.
      </p>
    </div>
  );
}
